import { ContactDto } from "../contact/models/contact"
import { Entity } from "../helpers/Entity"
import { PAYMENT_STATUS, PAYMENT_TYPE } from "../helpers/Payment"
import { ExternalLinkDto } from "../integration/ExternalLink"
import { PayinStatus } from "../payment-processor/rainforest/Payin"


export type PaymentAttributes = {
    status: string,
    type: string,
    createdAt: string,
    creditMemoId : string,
    capturedAt: string,
    deletedBy: string,
    succeededAt: string,
    fingerprintId: string,
    identifier: string,
    source: string,
    currency: string,
    paymentType?: PaymentTypeDto,
    amount: string,
    originalPaymentId: string,
    integratedPayment: IntegratedPayment,
    note: string,
    externalLink: ExternalLinkDto,
    totalRefundedAmount: string
    refunds: RefundTransactionDto[]
    refundedPayments: RefundTransactionDto[]
    terminalId: string
    applicationLabel: string
    contactId: string
    contact: ContactDto
}

type PaymentTypeAttributes = {
    icon: string,
    name: string
}
export type PaymentTypeDto = {
    id: string
    type: string
    attributes: PaymentTypeAttributes
}

export type PaymentDto = {
    id: string
    type: string
    attributes: PaymentAttributes
}
type IntegratedPaymentAttributes = {
    createdAt: string,
    amount: string,
    currency: string,
    status: string,
    integrationKey: string,
    hash: string,
    type: string,
    paymentFlowType: string,
    creditCardDetails?: CreditCardDetails,
    bankAccountDetails?: BankDetails,
    externalCheckoutDetails?: ExternalCheckoutDetails,
    declinedReason: string
}

export type CreditCardDetails = {
    attributes: CreditCardDetailsAttributes
}
type CreditCardDetailsAttributes = {
    last4: string,
    brand: string,
    cardHolderName: string,
    expirationMonth: string,
    expirationYear: string,
}

export type BankDetails = {
    attributes: BankDetailsAttributes
}

type BankDetailsAttributes = {
    bankName: string,
    accountHolderName: string,
    bankAccountToken: string,
    country: string,
    externalId: string,
    last4: string,
    routingNumber: string,
}

export type ExternalCheckoutDetails = {
    attributes: ExternalCheckoutDetailsAttributes
}

type ExternalCheckoutDetailsAttributes = {
    paymentType: string,
}

type IntegratedPayment = {
    id: string,
    type: string,
    attributes: IntegratedPaymentAttributes,
}

type RefundTransactionAttributes = {
    amount: number,
    currency: string,
    paymentId: string
}

type RefundTransactionDto = {
    id: string
    attributes: RefundTransactionAttributes
}


export default class Payment implements Entity {
    private dto: PaymentDto

    constructor(dto: PaymentDto) {
        this.dto = dto
    }

    isLoaded(): boolean {
        return this.dto.type === "payments"
    }

    getPayment(): PaymentDto {
        return this.dto
    }

    getId(): string {
        return this.dto.id
    }


    getIdentifier(): string {
        return this.dto.attributes.identifier
    }

    getSource(): string {
        return this.dto.attributes.source
    }

    getPaymentTypeIcon(): string {
        return this.dto.attributes.paymentType?.attributes.icon as string
    }
    getNote(): string {
        return this.dto.attributes.note
    }

    getAmount(): string {
        return this.dto.attributes.amount
    }

    getStatus(): string {
        return this.dto.attributes.status
    }

    getType(): string {
        return this.dto.attributes.type
    }

    getCurrency(): string {
        return this.dto.attributes.currency
    }

    getCapturedAt(): string {
        return this.dto.attributes.capturedAt
    }

    getSucceededAt(): string {
        return this.dto.attributes.succeededAt
    }

    getFingerprintId(): string {
        return this.dto.attributes.fingerprintId
    }

    getOriginalPaymentId(): string {
        return this.dto.attributes.originalPaymentId
    }

    getCreditMemoId(): string {
        return this.dto.attributes.creditMemoId
    }

    getDeletedBy() : string {
        return this.dto.attributes.deletedBy
    }

    getTotalRefundedAmount(): number {
        return Number(this.dto.attributes.totalRefundedAmount)
    }

    getRefunds(): RefundTransactionDto[] {
        return this.dto.attributes.refunds
    }

    getRefundedPayments(): RefundTransactionDto[] {
        return this.dto.attributes.refundedPayments
    }

    getTerminalId(): string {
        return this.dto.attributes.terminalId
    }

    getDeclinedReason(): string {
        return this.dto.attributes.integratedPayment.attributes.declinedReason
    }

    getBankAccountLast4(): string | undefined {
        return this.dto.attributes.integratedPayment.attributes.bankAccountDetails?.attributes?.last4
    }

    isApproved(): boolean {
        return ![
            PAYMENT_STATUS.DECLINED,
            PAYMENT_STATUS.VOIDED,
            PAYMENT_STATUS.REFUNDED,
            PAYMENT_STATUS.EXPIRED,
            PAYMENT_STATUS.TRANSFERRED,
        ].includes(this.getStatus())
    }

    isProcessing(): boolean {
        return this.getIntegratedPaymentStatus() === PayinStatus.PROCESSING
    }

    getIntegratedPaymentStatus(): string {
        return this.dto.attributes.integratedPayment?.attributes?.status ?? ""
    }

    getRefundableAmount(): number {
        const amount = Number(this.dto.attributes.amount)
        const refundedAmount = Number(this.dto.attributes.totalRefundedAmount)

        //only for new refund work flow
        //refunding as on account will generate an element in the refunded payments array and cause the refunable amount to display zero
        const isCreditGeneratedFromReturn =
        this.dto.attributes.type === PAYMENT_TYPE.CREDIT && amount < 0 &&
        (!this.dto.attributes?.refundedPayments || this.dto.attributes?.refundedPayments?.length === 0)
        if (isCreditGeneratedFromReturn) {
            return (amount * -1) - refundedAmount
        }

        return amount < 0 ? refundedAmount : amount - refundedAmount
    }

    getIntegratedPaymentCreditCardDetails(): CreditCardDetails | undefined {
        return this.dto.attributes.integratedPayment?.attributes?.creditCardDetails ?? undefined
    }

    getIntegratedPaymentBankDetails(): BankDetails | undefined {
        return this.dto.attributes.integratedPayment?.attributes?.bankAccountDetails ?? undefined
    }

    getIntegratedPaymentIntegrationKey(): string {
        return this.dto.attributes.integratedPayment?.attributes?.integrationKey ?? ""
    }

    getIntegratedPaymentType(): string {
        return this.dto.attributes.integratedPayment?.attributes?.type ?? ""
    }

    getIntegratedPayment(): IntegratedPayment {
        return this.dto.attributes.integratedPayment ??  undefined
    }

    getExternalLink(): ExternalLinkDto {
        return this.dto.attributes.externalLink ?? undefined
    }

    getContactId(): string {
        return this.dto.attributes.contactId
    }

    getContact(): ContactDto {
        return this.dto.attributes.contact
    }

    getContactName(): string {
        return this.getContact().attributes.name
    }
}


// WEBPACK FOOTER //
// ./src/state/payment/Payment.ts